import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import axios from 'axios'

const DEFAULT_LEFT  = ['pending', 'heatmap', 'workload', 'activity']
const DEFAULT_RIGHT = ['changes', 'upcoming', 'topforms', 'quickactions']
const ALL_DEFAULTS  = [...DEFAULT_LEFT, ...DEFAULT_RIGHT]

export const useOverviewStore = defineStore('overview', () => {
    // ── State ────────────────────────────────────────────────────────────────
    const period        = ref('tyden')
    const leftWidgets   = ref([...DEFAULT_LEFT])
    const rightWidgets  = ref([...DEFAULT_RIGHT])
    const hiddenWidgets = ref([])
    const editMode      = ref(false)
    const kpiData       = ref(null)
    const workloadData  = ref(null)
    const heatmapData   = ref(null)

    // ── Computed ─────────────────────────────────────────────────────────────
    const currentKpi      = computed(() => kpiData.value?.[period.value] ?? null)
    const currentWorkload = computed(() => workloadData.value?.[period.value] ?? [])
    const currentHeatmap  = computed(() => heatmapData.value?.[period.value] ?? {})

    const visibleLeft  = computed(() => leftWidgets.value.filter(id => !hiddenWidgets.value.includes(id)))
    const visibleRight = computed(() => rightWidgets.value.filter(id => !hiddenWidgets.value.includes(id)))

    // ── Actions ──────────────────────────────────────────────────────────────
    function setPeriod(p) {
        period.value = p
    }

    function setKpiData(data) {
        kpiData.value = data
    }

    function setWorkloadData(data) {
        workloadData.value = data
    }

    function setHeatmapData(data) {
        heatmapData.value = data
    }

    function toggleEditMode() {
        editMode.value = !editMode.value
    }

    function hideWidget(id) {
        if (!hiddenWidgets.value.includes(id)) {
            hiddenWidgets.value = [...hiddenWidgets.value, id]
        }
        saveLayout()
    }

    function showWidget(id) {
        hiddenWidgets.value = hiddenWidgets.value.filter(w => w !== id)
        // Add to the shorter column
        if (visibleLeft.value.length <= visibleRight.value.length) {
            leftWidgets.value = [...leftWidgets.value, id]
        } else {
            rightWidgets.value = [...rightWidgets.value, id]
        }
        saveLayout()
    }

    function loadLayout(settings) {
        if (settings?.leftWidgets?.length || settings?.rightWidgets?.length) {
            // New two-column format
            const savedLeft  = settings.leftWidgets  ?? []
            const savedRight = settings.rightWidgets ?? []
            const allSaved   = [...savedLeft, ...savedRight]
            const missing    = ALL_DEFAULTS.filter(id => !allSaved.includes(id))
            leftWidgets.value  = [...savedLeft,  ...missing.filter((_, i) => i % 2 === 0)]
            rightWidgets.value = [...savedRight, ...missing.filter((_, i) => i % 2 !== 0)]
        } else if (settings?.widgetOrder?.length) {
            // Migrate from old flat-array format
            const saved   = settings.widgetOrder
            const missing = ALL_DEFAULTS.filter(id => !saved.includes(id))
            const all     = [...saved, ...missing]
            leftWidgets.value  = all.filter((_, i) => i % 2 === 0)
            rightWidgets.value = all.filter((_, i) => i % 2 !== 0)
        }
        if (settings?.hiddenWidgets) {
            hiddenWidgets.value = settings.hiddenWidgets
        }
    }

    async function saveLayout() {
        try {
            await axios.patch(route('app.overview.settings'), {
                leftWidgets:   leftWidgets.value,
                rightWidgets:  rightWidgets.value,
                hiddenWidgets: hiddenWidgets.value,
            })
        } catch (e) {
            // Silently fail — layout preference is non-critical
        }
    }

    return {
        period,
        leftWidgets,
        rightWidgets,
        hiddenWidgets,
        editMode,
        kpiData,
        workloadData,
        heatmapData,
        currentKpi,
        currentWorkload,
        currentHeatmap,
        visibleLeft,
        visibleRight,
        setPeriod,
        setKpiData,
        setWorkloadData,
        setHeatmapData,
        toggleEditMode,
        hideWidget,
        showWidget,
        loadLayout,
        saveLayout,
        ALL_DEFAULTS,
    }
})
